import { FC, useEffect, useMemo, useState } from 'react';
import cn from 'classnames';
import { relevanceOptions, RELEVANCES } from 'constants/date';
import dayjs from 'dayjs';
import { ILayerQuickFilterSettings } from 'interfaces/localStorage';
import { TDateRange } from 'types';

import { TimelineTooltip } from 'components/Map/TimelineTooltip';
import TreeSelectWithDatepicker from 'components/ui/TreeSelectWithDatepicker';
import {
  getDateRangeFromRelevance,
  getDisplayRelevanceOrDate,
  useLocalStorageDates,
} from 'utils';

import { CreatedAtFilter } from '../types';

interface DateFilterSelectorProps {
  filterSettings: ILayerQuickFilterSettings;
  setFilterSettings: (settings: ILayerQuickFilterSettings) => void;
  onCreatedAtFilterChange: (createdAtFilter: CreatedAtFilter) => void;
  disabled?: boolean;
}

export const DateFilterSelector: FC<DateFilterSelectorProps> = ({
  filterSettings,
  setFilterSettings,
  onCreatedAtFilterChange,
  disabled,
}) => {
  const [showSelectorDatepicker, setShowSelectorDatepicker] = useState(false);
  const minDate = new Date(2022, 1, 24);
  const currentDate = new Date();

  const { relevance } = filterSettings;
  const localStorageDates = useLocalStorageDates(filterSettings);
  const [startDate, endDate] = localStorageDates;
  const dateFilterDisplayValue = useMemo(
    () => getDisplayRelevanceOrDate(relevance, startDate, endDate),
    [relevance, startDate, endDate]
  );

  const handleSetDates = (relevance: string, range: TDateRange) => {
    const [startDate, endDate] = range;
    const isRangeValid = startDate && endDate;

    setFilterSettings({
      ...filterSettings,
      startDate,
      endDate,
      relevance,
    });

    isRangeValid &&
      onCreatedAtFilterChange({
        relevance,
        value: [
          dayjs(startDate).startOf('day').utc(true).toDate(),
          dayjs(endDate).endOf('day').utc(true).toDate(),
        ],
      });
  };

  const handleSetDateFilter = (relevance: string) => {
    const isDefaultRelevance = relevance === RELEVANCES.OTHER;

    setShowSelectorDatepicker(isDefaultRelevance);

    if (isDefaultRelevance) {
      setFilterSettings({
        ...filterSettings,
        relevance,
      });
    } else {
      const dateRange = getDateRangeFromRelevance(relevance);

      handleSetDates(relevance, dateRange ?? localStorageDates);
    }
  };

  const handleDateChange = (range: TDateRange) => {
    const [startDate, endDate] = range;
    const isRangeValid = startDate && endDate;

    handleSetDates(relevance, range);
    isRangeValid && setShowSelectorDatepicker(false);
  };

  const handleCalendarClose = () => {
    if (startDate && !endDate) {
      handleSetDates(relevance, [startDate, startDate]);
    }
  };

  useEffect(() => {
    disabled && setShowSelectorDatepicker(false);
  }, [disabled]);

  return (
    <>
      <div
        className={cn(
          'tpg-c2 hover:cursor-pointer text-bright_product',
          { 'hover:text-dark_product': !disabled && !showSelectorDatepicker },
          { 'text-dark_product': showSelectorDatepicker },
          { 'text-tpg_light': disabled }
        )}
        onClick={() =>
          !disabled && setShowSelectorDatepicker(!showSelectorDatepicker)
        }
      >
        <TimelineTooltip isTimeline={disabled}>
          <div>{dateFilterDisplayValue}</div>
        </TimelineTooltip>
      </div>
      <div className="absolute w-0 h-0">
        {showSelectorDatepicker && !disabled && (
          <TreeSelectWithDatepicker
            datepickerValue={RELEVANCES.OTHER}
            selectorValue={relevance}
            selectorOptions={relevanceOptions}
            onSelect={handleSetDateFilter}
            minDate={minDate}
            maxDate={currentDate}
            startDate={startDate}
            endDate={endDate}
            setDate={handleDateChange}
            onCalendarClose={handleCalendarClose}
            visibleBackPropagate={setShowSelectorDatepicker}
            menuPlacement="bottom"
            optionsClassName="!relative !top-6 left-0 !w-[336px]"
            datepickerClassName="border-solid border-[1px] border-tpg_light rounded-[10px] -left-4 !top-4 !z-50"
          />
        )}
      </div>
    </>
  );
};
